import Ionicons from "@expo/vector-icons/Ionicons";
import { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { SafeAreaView } from "react-native-safe-area-context";

import {
  getFreePlanFeatures,
  getLocalPlusPricing,
  getPlusPlanFeatures,
} from "@/constants/plans";
import { formatAmountInCurrency } from "@/lib/payments/prices";
import { Radius, Spacing } from "@/constants/theme";
import { useBillingRegion } from "@/lib/billing-region";
import { notify } from "@/lib/dialog";
import { getPaymentProvider } from "@/lib/payments";
import type { PlanPrices } from "@/lib/payments/types";
import { useTrialOffer } from "@/lib/trial-offer";
import { useAuthStore } from "@/state/auth-store";
import { useTheme } from "@/hooks/use-theme";
import type { ThemeColors } from "@/constants/theme";
import i18n from "@/i18n";
import { useTranslation } from "react-i18next";

type Plan = "free" | "plus";
type BillingInterval = "monthly" | "annual";

const FONT_FAMILY = Platform.select({
  ios: "Avenir Next",
  android: "sans-serif",
  default: undefined,
});

function formatPlanDate(iso?: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function PricingScreen() {
  useTranslation();
  const router = useRouter();
  const c = useTheme();
  const { width } = useWindowDimensions();
  const styles = useMemo(() => createStyles(c), [c]);
  const isWide = width >= 820;

  const userPlan = useAuthStore((state) =>
    state.user?.plan === "plus" ? "plus" : "free",
  );
  const plusSource = useAuthStore((state) => state.user?.plusSource);
  const plusGrantUntil = useAuthStore((state) => state.user?.plusGrantUntil);
  const hasSubscription = plusSource === "subscription";
  const refreshUser = useAuthStore((state) => state.refreshUser);
  // The paywall's front door re-asks the server before showing an upsell: a
  // user granted Plus since the app last looked should see their real state,
  // not a pitch. The double-payment case is already refused server-side
  // (`assertNotAlreadySubscribed`) — this is about not pitching to a
  // subscriber. Best-effort: a failed refresh shows the screen as it was.
  useEffect(() => {
    refreshUser().catch(() => {});
  }, [refreshUser]);
  const [busy, setBusy] = useState<Plan | null>(null);
  // Billing interval is the user's choice; annual is the default (best value),
  // but nobody is forced onto it — they can switch to monthly.
  const [billingInterval, setBillingInterval] =
    useState<BillingInterval>("annual");
  // Currency comes from the server's location resolution (£/€/$), not the
  // device locale. Until it answers, prices show as $ — the same NUMBER in
  // every currency, so only the symbol can change under the reader.
  const { currency } = useBillingRegion();
  const localPricing = useMemo(() => getLocalPlusPricing(currency), [currency]);

  // On native the STORE decides the price, and it is not our number: web is
  // 7.99/69.99, the App Store is 8.99/79.99, and Apple converted every
  // storefront we did not price by hand. Rendering `localPricing` here would
  // quote a price the buyer will not be charged.
  //
  // `resolved` is tracked separately from the value because "asked, and the
  // store had no answer" and "haven't asked yet" have to look different: the
  // first shows the no-price state, the second shows nothing changing.
  const [storePrices, setStorePrices] = useState<PlanPrices | null>(null);
  const [storePricesResolved, setStorePricesResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getPaymentProvider()
      .prices()
      .then((prices) => {
        if (cancelled) return;
        setStorePrices(prices);
        setStorePricesResolved(true);
      })
      .catch(() => {
        if (!cancelled) setStorePricesResolved(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Web: our numbers ARE the charge (Stripe holds them), so no store lookup is
  // involved and `localPricing` is authoritative. Native: the store or nothing.
  const displayPrices = useMemo(() => {
    if (Platform.OS === "web") {
      return {
        monthly: localPricing.monthly,
        annual: localPricing.annual,
        monthlyEquivalentAnnual: localPricing.monthlyEquivalentAnnual,
        savingsPercent: localPricing.savingsPercent,
        zero: `${localPricing.symbol}0`,
      };
    }
    if (!storePrices) return null;
    return {
      monthly: storePrices.monthly,
      annual: storePrices.annual,
      monthlyEquivalentAnnual: storePrices.monthlyEquivalentAnnual,
      savingsPercent: storePrices.savingsPercent,
      zero: formatAmountInCurrency(0, storePrices.currencyCode),
    };
  }, [localPricing, storePrices]);

  // The free-trial offer for THIS buyer, from the platform's own authority:
  // the API for web (one trial per Stripe customer), the store's introductory
  // offer on native. Null = no offer, and the page sells Plus at full price.
  // The shared session-cached offer every Plus gate pitches from; the
  // checkout page re-quotes the real first-charge date from Stripe's own
  // invoice preview before anything is created.
  const activeTrialOffer = useTrialOffer("plus_paywall_opened");

  const isAnnual = billingInterval === "annual";
  // No price rather than a wrong one. On native this is the state until the
  // store answers, and permanently if it never does — the buy button then
  // reports "unavailable" through the same provider, so the two agree.
  const plusPrice = displayPrices
    ? i18n.t(
        isAnnual ? "billing.pricing.perYear" : "billing.pricing.perMonth",
        { price: isAnnual ? displayPrices.annual : displayPrices.monthly },
      )
    : storePricesResolved
      ? i18n.t("billing.pricing.priceUnavailable")
      : "";
  const plusTrialNote =
    activeTrialOffer && displayPrices
      ? i18n.t("billing.trial.pricingNote", {
          days: activeTrialOffer.days,
          zero: displayPrices.zero,
          price: isAnnual ? displayPrices.annual : displayPrices.monthly,
          interval: i18n.t(
            isAnnual
              ? "billing.checkoutPage.perYear"
              : "billing.checkoutPage.perMonth",
          ),
          date: activeTrialOffer.firstCharge,
        })
      : undefined;
  const plusGrantDate = formatPlanDate(plusGrantUntil);
  const plusGrantNote =
    plusSource === "grant"
      ? plusGrantDate
        ? i18n.t("billing.pricing.grantActiveUntil", {
            date: plusGrantDate,
          })
        : i18n.t("billing.pricing.grantActive")
      : undefined;
  const plusActionLabel =
    userPlan === "plus"
      ? hasSubscription
        ? i18n.t("billing.pricing.cancel")
        : undefined
      : i18n.t(
          activeTrialOffer
            ? "billing.trial.startCta"
            : "billing.pricing.upgrade",
        );

  function close() {
    if (router.canGoBack()) {
      router.back();
      return;
    }

    router.replace("/profile" as never);
  }

  async function handlePlusAction() {
    if (busy) return;

    // A grant is active Plus, but it is not a provider subscription. There is
    // nothing to manage/cancel until the user explicitly starts a real trial or
    // paid subscription.
    if (userPlan === "plus" && !hasSubscription) return;

    // New web subscriptions are taken in-app on /subscribe rather than by
    // redirecting to Stripe's hosted page. Native still goes through the store
    // (StoreKit / Play Billing), which Apple and Google require, and managing an
    // existing subscription still uses the provider's portal on every platform.
    if (Platform.OS === "web" && userPlan !== "plus") {
      router.push(
        `/subscribe?plan=${isAnnual ? "plus_annual" : "plus_monthly"}`,
      );
      return;
    }

    setBusy("plus");

    try {
      const provider = getPaymentProvider();
      const result =
        userPlan === "plus"
          ? await provider.manage()
          : await provider.purchase(isAnnual ? "plus_annual" : "plus_monthly");

      switch (result.status) {
        case "success":
          // The plan flips server-side when the provider's webhook lands —
          // for store purchases that's usually a few seconds behind the
          // purchase sheet. Poll briefly (bounded) so the screen updates
          // without a manual refresh; if the webhook is slower, the next
          // refresh anywhere picks it up.
          await refreshUser();
          for (let attempt = 0; attempt < 4; attempt += 1) {
            if (useAuthStore.getState().user?.plan === "plus") break;
            await new Promise((resolve) => setTimeout(resolve, 1500));
            await refreshUser();
          }
          notify(
            i18n.t("billing.pricing.activeTitle"),
            i18n.t("billing.pricing.activeBody"),
          );
          break;
        case "pending":
        case "cancelled":
          break;
        case "failed":
          notify(
            i18n.t("billing.pricing.paymentError"),
            result.message ?? i18n.t("billing.common.tryAgainLater"),
          );
          break;
        case "unavailable":
          notify(
            i18n.t("billing.pricing.unavailableTitle"),
            result.message ?? i18n.t("billing.pricing.unavailableBody"),
          );
          break;
      }
    } catch {
      notify(
        i18n.t("billing.pricing.paymentError"),
        i18n.t("billing.common.tryAgainLater"),
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <SafeAreaView style={styles.safe} edges={["top"]}>
      <StatusBar style="auto" />

      <View style={styles.topBar}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={i18n.t("billing.common.goBackA11y")}
          onPress={close}
          hitSlop={10}
          style={({ pressed }) => [
            styles.iconButton,
            pressed && styles.pressed,
          ]}
        >
          <Ionicons name="chevron-back" size={23} color={c.tint} />
        </Pressable>

        <Text style={styles.topBarTitle}>
          {i18n.t("billing.pricing.title")}
        </Text>

        <View style={styles.headerSpacer} />
      </View>

      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.content}
      >
        <View style={styles.intro}>
          <Text style={styles.title}>{i18n.t("billing.pricing.intro")}</Text>
        </View>

        {userPlan !== "plus" ? (
          <View style={styles.segment}>
            {(["monthly", "annual"] as const).map((option) => {
              const active = billingInterval === option;
              return (
                <Pressable
                  key={option}
                  accessibilityRole="button"
                  accessibilityState={{ selected: active }}
                  onPress={() => setBillingInterval(option)}
                  style={[styles.segmentBtn, active && styles.segmentBtnActive]}
                >
                  <Text
                    style={[
                      styles.segmentText,
                      active && styles.segmentTextActive,
                    ]}
                  >
                    {i18n.t(
                      option === "monthly"
                        ? "billing.pricing.monthly"
                        : "billing.pricing.annual",
                    )}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        ) : null}

        <View
          style={[
            styles.planGrid,
            isWide ? styles.planGridWide : styles.planGridStack,
          ]}
        >
          {/* Plus leads: it is the plan this page exists to sell, and on
              narrow screens it is the one card guaranteed above the fold. */}
          <PlanCard
            title=""
            price={plusPrice}
            trialNote={plusGrantNote ?? plusTrialNote}
            description={i18n.t("billing.pricing.plusDescription")}
            features={getPlusPlanFeatures()}
            highlighted
            current={userPlan === "plus"}
            actionLabel={plusActionLabel}
            busy={busy === "plus"}
            onAction={plusActionLabel ? handlePlusAction : undefined}
            colors={c}
            styles={styles}
          />

          <PlanCard
            title={i18n.t("billing.common.free")}
            // Free is free in every currency, so this can always render — but
            // it follows whatever currency the Plus card resolved to, rather
            // than sitting in $ beside a price in kr.
            price={displayPrices?.zero ?? `${localPricing.symbol}0`}
            description={i18n.t("billing.pricing.freeDescription")}
            features={getFreePlanFeatures()}
            current={userPlan === "free"}
            colors={c}
            styles={styles}
          />
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

function PlanCard({
  title,
  price,
  trialNote,
  description,
  features,
  highlighted = false,
  current,
  actionLabel,
  busy = false,
  onAction,
  colors,
  styles,
}: {
  title: string;
  price: string;
  /** The free-trial pitch ("£0 today …"); rendered emphasized under the price. */
  trialNote?: string;
  description: string;
  features: string[];
  highlighted?: boolean;
  current: boolean;
  actionLabel?: string;
  busy?: boolean;
  onAction?: () => void;
  colors: ThemeColors;
  styles: ReturnType<typeof createStyles>;
}) {
  return (
    <View style={[styles.planCard, highlighted && styles.planCardHighlighted]}>
      <View style={styles.planHeader}>
        <View style={styles.planIdentity}>
          <Text style={styles.planPrice}>{price}</Text>
          {title ? <Text style={styles.planTitle}>{title}</Text> : null}
        </View>

        {current ? (
          <View style={styles.currentBadge}>
            <Text style={styles.currentBadgeText}>
              {i18n.t("billing.common.currentPlan")}
            </Text>
          </View>
        ) : null}
      </View>
      {trialNote ? <Text style={styles.planTrialNote}>{trialNote}</Text> : null}

      {onAction && actionLabel ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={actionLabel}
          disabled={busy}
          onPress={onAction}
          style={({ pressed }) => [
            styles.actionButton,
            busy && styles.buttonDisabled,
            pressed && !busy && styles.pressed,
          ]}
        >
          {busy ? (
            <ActivityIndicator size="small" color={colors.onTint} />
          ) : (
            <Text style={styles.actionButtonText}>{actionLabel}</Text>
          )}
        </Pressable>
      ) : null}

      <Text style={styles.planDescription}>{description}</Text>

      <View style={styles.featureList}>
        {features.map((feature) => (
          <View key={feature} style={styles.featureRow}>
            <Ionicons name="checkmark-circle" size={18} color={colors.tint} />
            <Text style={styles.featureText}>{feature}</Text>
          </View>
        ))}
      </View>
    </View>
  );
}

function createStyles(colors: ThemeColors) {
  return StyleSheet.create({
    safe: {
      flex: 1,
      backgroundColor: colors.background,
    },
    topBar: {
      minHeight: 56,
      paddingHorizontal: Spacing.md,
      flexDirection: "row",
      alignItems: "center",
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border,
      backgroundColor: colors.background,
    },
    iconButton: {
      width: 40,
      height: 40,
      borderRadius: 20,
      alignItems: "center",
      justifyContent: "center",
    },
    topBarTitle: {
      flex: 1,
      color: colors.text,
      fontFamily: FONT_FAMILY,
      fontSize: 16,
      fontWeight: "700",
      textAlign: "center",
    },
    headerSpacer: {
      width: 40,
    },
    content: {
      width: "100%",
      maxWidth: 980,
      alignSelf: "center",
      paddingHorizontal: Spacing.xl,
      paddingTop: Spacing.xl,
      paddingBottom: Spacing.xxxl,
      gap: Spacing.xl,
    },
    intro: {
      maxWidth: 640,
      alignSelf: "center",
      alignItems: "center",
    },
    segment: {
      flexDirection: "row",
      alignSelf: "center",
      padding: 4,
      gap: 4,
      borderRadius: Radius.pill,
      backgroundColor: colors.surfaceAlt,
    },
    segmentBtn: {
      minWidth: 104,
      paddingVertical: 8,
      paddingHorizontal: Spacing.md,
      borderRadius: Radius.pill,
      alignItems: "center",
      justifyContent: "center",
    },
    segmentBtnActive: {
      backgroundColor: colors.tint,
    },
    segmentText: {
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      fontWeight: "700",
    },
    segmentTextActive: {
      color: colors.onTint,
    },
    title: {
      color: colors.text,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 18,
      fontWeight: "400",
      textAlign: "center",
    },
    planGrid: {
      width: "100%",
      gap: Spacing.lg,
      alignItems: "stretch",
    },
    planGridWide: {
      flexDirection: "row",
    },
    planGridStack: {
      flexDirection: "column",
    },
    planCard: {
      flex: 1,
      minWidth: 0,
      padding: Spacing.xl,
      gap: Spacing.sm,
      borderRadius: Radius.sm,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    planCardHighlighted: {
      borderColor: colors.tint,
    },
    planHeader: {
      flexDirection: "row",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: Spacing.sm,
    },
    planIdentity: {
      flex: 1,
      minWidth: 0,
      flexDirection: "row",
      alignItems: "baseline",
      flexWrap: "wrap",
      gap: Spacing.sm,
    },
    currentBadge: {
      minHeight: 28,
      paddingHorizontal: Spacing.md,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: Radius.pill,
      backgroundColor: colors.backgroundSelected,
    },
    currentBadgeText: {
      color: colors.tint,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      fontWeight: "800",
    },
    planTitle: {
      color: colors.text,
      fontFamily: FONT_FAMILY,
      fontSize: 19,
      lineHeight: 25,
      fontWeight: "800",
    },
    planPrice: {
      color: colors.text,
      fontFamily: FONT_FAMILY,
      fontSize: 19,
      lineHeight: 25,
      fontWeight: "700",
    },
    // The trial pitch reads as body text, not another shouting tint line —
    // the CTA ("Start free trial") already carries the emphasis.
    planTrialNote: {
      marginTop: -Spacing.xs,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 18,
    },
    planDescription: {
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 19,
    },
    featureList: {
      gap: Spacing.sm,
      paddingTop: Spacing.sm,
    },
    featureRow: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: Spacing.sm,
    },
    featureText: {
      flex: 1,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      lineHeight: 20,
    },
    actionButton: {
      minHeight: 46,
      marginTop: Spacing.xs,
      marginBottom: Spacing.sm,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: Radius.sm,
      backgroundColor: colors.tint,
    },
    actionButtonText: {
      color: colors.onTint,
      fontFamily: FONT_FAMILY,
      fontSize: 15,
      fontWeight: "800",
    },
    buttonDisabled: {
      opacity: 0.65,
    },
    pressed: {
      opacity: 0.72,
    },
  });
}
